import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
// @ts-ignore
import * as Validator from 'validatorjs';
import { toJS } from 'mobx';

import { Radio, Tooltip } from '@gamesb42/ui-kit';
import { Title3 } from '../Typography';
import TextInput from '../TextInput';
import { about } from './index';
import { director } from '../Director';
import info from './assets/info.svg';
import styles from './styles.module.scss';

// Типы компаний
type CompanyForm = 'corporation' | 'sole-proprietor' | 'partnership';

// Компонент для поля с tooltip
const FieldWithTooltip = ({
  children,
  tooltipText,
  tooltipPosition = { top: '-70px', right: '-620px' },
}: {
  children: React.ReactNode;
  tooltipText: string;
  tooltipPosition?: { top: string; right: string };
}) => (
  <div style={{ height: '60px', marginBottom: '32px' }}>
    {children}
    <div
      style={{
        position: 'relative',
        top: tooltipPosition.top,
        right: tooltipPosition.right,
        cursor: 'pointer',
      }}
    >
      <Tooltip
        title={tooltipText}
        placement="leftTop"
        children={<img src={info} alt="info" />}
      />
    </div>
  </div>
);

// Переиспользуемая форма для корпоративной информации
const CorporateInfoForm = ({ validation }: { validation: any }) => (
  <div>
    <div style={{ marginBottom: '20px' }}>
      <Title3 className={styles.subTitle}>Company info*</Title3>
    </div>

    <FieldWithTooltip tooltipText="Please provide the full name of the registered company including the corporate form (e.g. Apple inc)">
      <TextInput
        onChange={(e) => {
          about.values.details.legal_name = e;
        }}
        className={styles.textField}
        label="Company legal name*"
        dataTest="Company legal name"
        value={about.values.details.legal_name}
        error={validation.errors.first('legal_name')}
      />
    </FieldWithTooltip>

    <TextInput
      onChange={(e) => {
        about.values.details.email = e;
      }}
      label="Company corporate email*"
      dataTest="Company corporate email"
      className={styles.textField}
      value={about.values.details.email}
      error={validation.errors.first('email')}
    />

    <FieldWithTooltip tooltipText="Phone number with country code">
      <TextInput
        onChange={(e) => {
          about.values.details.phone = e;
        }}
        label="Company phone number"
        dataTest="Company phone number"
        value={about.values.details.phone}
        error={validation.errors.first('phone')}
      />
    </FieldWithTooltip>

    <TextInput
      onChange={(e) => {
        about.values.details.website = e;
      }}
      dataTest="Company website"
      label="Company website"
      value={about.values.details.website}
      error={validation.errors.first('website')}
    />
  </div>
);

// Переиспользуемая форма для ИП
const IndividualEntrepreneurForm = ({ validation }: { validation: any }) => (
  <div>
    <div style={{ marginBottom: '20px' }}>
      <Title3 className={styles.subTitle}>Individual Entrepreneur info*</Title3>
    </div>

    <TextInput
      onChange={(e) => {
        about.values.details.legal_name = e;
      }}
      label="IE Full name*"
      dataTest="Company legal name"
      className={styles.textField}
      value={about.values.details.legal_name}
      error={validation.errors.first('legal_name')}
    />

    <TextInput
      onChange={(e) => {
        about.values.details.email = e;
      }}
      label="IE email*"
      dataTest="Company corporate email"
      className={styles.textField}
      value={about.values.details.email}
      error={validation.errors.first('email')}
    />

    <FieldWithTooltip tooltipText="Phone number with country code">
      <TextInput
        onChange={(e) => {
          about.values.details.phone = e;
        }}
        className={styles.textField}
        label="IE phone number*"
        dataTest="Company phone number"
        value={about.values.details.phone}
        error={validation.errors.first('phone')}
      />
    </FieldWithTooltip>

    <TextInput
      onChange={(e) => {
        about.values.details.website = e;
      }}
      dataTest="Company website url"
      label="IE website"
      value={about.values.details.website}
      error={validation.errors.first('website')}
    />
  </div>
);

const Company = observer(() => {
  // Правила валидации
  const validationRules = {
    legal_name: 'required|string',
    email: 'required|email',
    phone: [
      'regex:/^[\\+]?[(]?[0-9]{3}[)]?[-\\s\\.]?[0-9]{3}[-\\s\\.]?[0-9]{4,8}$/',
    ],
    website:
      'regex:/[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)?/gi',
    company_form: 'required',
  };

  const validation = useMemo(
    () =>
      new Validator(toJS(about.values.details), validationRules, {
        required: 'This field is required',
        email: 'The email format is invalid',
        digits_between: 'Field should contain from 10 up to 14 digits only',
        website:
          'The website format is invalid. For example: https://www.example.com',
        'regex.phone': '+(country code) phone number',
      }),
    [
      about.values.details.legal_name,
      about.values.details.email,
      about.values.details.phone,
      about.values.details.website,
      about.values.details.company_form,
    ],
  );

  const isValid = validation.passes();

  useEffect(() => {
    about.isStepIsValid[1] = isValid;
  }, [isValid]);

  // Обработчик изменения формы компании
  const handleCompanyFormChange = (form: CompanyForm) => {
    about.values.details.company_form = form;
    director.clearData();
  };

  const isCorporateEntity = ['partnership', 'corporation'].includes(
    about.values.details.company_form ?? '',
  );
  const isSoleProprietor =
    about.values.details.company_form === 'sole-proprietor';

  return (
    <>
      <div>
        <Title3 className={styles.subTitle}>Company form*</Title3>
        <div className={styles.form}>
          <Radio.Group value={about.values.details.company_form}>
            <Radio
              onChange={() => handleCompanyFormChange('corporation')}
              value="corporation"
            >
              Corporation
            </Radio>
            <Radio
              value="sole-proprietor"
              onChange={() => handleCompanyFormChange('sole-proprietor')}
            >
              Individual Entrepreneur (IE)
            </Radio>
            <Radio
              onChange={() => handleCompanyFormChange('partnership')}
              value="partnership"
            >
              Partnership
            </Radio>
          </Radio.Group>
        </div>
      </div>

      {/* Форма для корпоративных лиц */}
      {isCorporateEntity && <CorporateInfoForm validation={validation} />}

      {/* Форма для ИП */}
      {isSoleProprietor && (
        <IndividualEntrepreneurForm validation={validation} />
      )}
    </>
  );
});

export default Company;
